/**
 * Anonymous User Authentication Utilities
 * Manages preferences for guest/anonymous users
 */

const ANONYMOUS_PREFERENCES_KEY = 'anonymous_user_preferences';

/**
 * Set anonymous user preferences (curriculum, grade, user type)
 * @param {Object} preferences - { userType, boardId, gradeId, boardName, gradeName }
 */
export const setAnonymousUserPreferences = (preferences) => {
  try {
    localStorage.setItem(ANONYMOUS_PREFERENCES_KEY, JSON.stringify({
      ...preferences,
      timestamp: new Date().toISOString()
    }));
  } catch (error) {
    console.error('Error saving anonymous user preferences:', error);
  }
};

/**
 * Get anonymous user preferences
 * @returns {Object|null} - Preferences object or null if not set
 */
export const getAnonymousUserPreferences = () => {
  try {
    const stored = localStorage.getItem(ANONYMOUS_PREFERENCES_KEY);
    if (!stored) return null;
    return JSON.parse(stored);
  } catch (error) {
    console.error('Error loading anonymous user preferences:', error);
    return null;
  }
};

/**
 * Clear anonymous user preferences (when user signs up or logs in)
 */
export const clearAnonymousUserPreferences = () => {
  try {
    localStorage.removeItem(ANONYMOUS_PREFERENCES_KEY);
  } catch (error) {
    console.error('Error clearing anonymous user preferences:', error);
  }
};

/**
 * Check if user is anonymous (not authenticated)
 * @param {string} portal - Portal type ('student' or 'teacher')
 * @returns {boolean}
 */
export const isAnonymousUser = (portal) => {
  try {
    // Check if there's an auth token
    const tokenKey = `${portal}_token`;
    const token = localStorage.getItem(tokenKey);
    return !token;
  } catch (error) {
    return true;
  }
};

/**
 * Check if anonymous user has completed onboarding
 * @returns {boolean}
 */
export const hasAnonymousOnboarding = () => {
  const preferences = getAnonymousUserPreferences();
  return !!(preferences && preferences.userType && preferences.boardId && preferences.gradeId);
};

/**
 * Get board and grade IDs for anonymous user
 * Falls back to user data if authenticated
 * @param {Function} getUserData - Function to get authenticated user data
 * @param {string} portal - Portal type
 * @returns {Object} - { boardId, gradeId }
 */
export const getBoardAndGrade = (getUserData, portal) => {
  // First check if user is authenticated
  const userData = getUserData ? getUserData(portal) : null;
  
  if (userData && userData.board_id && userData.grade_id) {
    return {
      boardId: userData.board_id,
      gradeId: userData.grade_id
    };
  }
  
  // Fall back to anonymous preferences
  const anonymousPrefs = getAnonymousUserPreferences();
  if (anonymousPrefs) {
    return {
      boardId: anonymousPrefs.boardId,
      gradeId: anonymousPrefs.gradeId
    };
  }
  
  return {
    boardId: null,
    gradeId: null
  };
};
